import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getInvoice, openInvoicePdf } from '../../api/invoices';
import { useToast } from '../../components/ToastContext';
import { sanitizeCustomerInvoiceLineItems } from '../../../utils/customerPricingVisibility';

const formatDate = (value) => {
  if (!value) return 'TBD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const formatAmount = (currency, amount) => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return `${currency || ''} ${amount || ''}`.trim();
  return `${currency || ''} ${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
};

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getInvoice(invoiceId, null, { asGuest: true });
        if (!active) return;
        setInvoice(data);
      } catch (err) {
        if (!active) return;
        setError(err.message || 'Unable to load invoice');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [invoiceId]);

  const totals = invoice?.totals || {};
  const totalValue = totals.grandTotal ?? totals.total ?? 0;
  const handleDownload = async () => {
    if (!invoiceId) return;
    try {
      await openInvoicePdf(invoiceId, null, { asGuest: true, download: true });
    } catch (err) {
      if (err.code === 'POPUP_BLOCKED') {
        toast.error('Popup blocked. Please allow popups to download invoices.');
        return;
      }
      toast.error(err.message || 'Unable to open invoice');
    }
  };

  const handlePrint = async () => {
    if (!invoiceId) return;
    try {
      await openInvoicePdf(invoiceId, null, { asGuest: true, print: true });
    } catch (err) {
      if (err.code === 'POPUP_BLOCKED') {
        toast.error('Popup blocked. Please allow popups to print invoices.');
        return;
      }
      toast.error(err.message || 'Unable to print invoice');
    }
  };

  const lineItems = useMemo(() => sanitizeCustomerInvoiceLineItems(invoice?.lineItems || []), [invoice]);
  const paymentBreakdown = invoice?.paymentBreakdown || {};

  if (loading) return <div className="p-4 text-sm text-slate-600">Loading invoice...</div>;
  if (error) return <div className="p-4 text-sm text-rose-600">{error}</div>;
  if (!invoice) return null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Invoice</p>
          <h2 className="text-2xl font-semibold text-slate-900">{invoice.invoiceNumber}</h2>
          <p className="text-sm text-slate-600">{invoice.invoiceType.replace(/_/g, ' ')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleDownload}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
          >
            Download PDF
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"
          >
            Print
          </button>
          <button
            type="button"
            onClick={() => navigate('/guest/invoices')}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
          >
            Back
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-500">Billed to</p>
          <p className="mt-2 text-sm font-semibold text-slate-900">{invoice.billedTo?.name || 'Guest'}</p>
          <p className="text-sm text-slate-600">{invoice.billedTo?.email || '-'}</p>
          <p className="text-sm text-slate-600">{invoice.billedTo?.phone || '-'}</p>
          <p className="text-sm text-slate-600">{invoice.billedTo?.address || '-'}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-500">Stay details</p>
          <p className="mt-2 text-sm font-semibold text-slate-900">{invoice.stayDetails?.propertyName || '-'}</p>
          <p className="text-sm text-slate-600">{invoice.stayDetails?.propertyLocation || '-'}</p>
          <p className="text-sm text-slate-600">
            {formatDate(invoice.stayDetails?.checkIn)} - {formatDate(invoice.stayDetails?.checkOut)}
          </p>
          <p className="text-sm text-slate-600">
            {invoice.stayDetails?.nights || '-'} nights | {invoice.stayDetails?.guests || '-'} guests
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3">
          <p className="text-sm font-semibold text-slate-900">Line items</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-slate-100">
              <tr>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Qty</th>
                <th className="px-4 py-3">Unit</th>
                <th className="px-4 py-3">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lineItems.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-4 text-slate-500">
                    No line items found.
                  </td>
                </tr>
              )}
              {lineItems.map((item, idx) => (
                <tr key={`${item.description}-${idx}`}>
                  <td className="px-4 py-3 text-slate-700">{item.description}</td>
                  <td className="px-4 py-3 text-slate-600">{item.quantity ?? item.qty ?? 1}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatAmount(invoice.currency, item.unitPrice ?? item.unit_price ?? 0)}
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    {formatAmount(invoice.currency, item.amount ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:col-span-2">
          <p className="text-xs uppercase tracking-wide text-slate-500">Payment info</p>
          <div className="mt-2 text-sm text-slate-700">
            <div>Provider: {invoice.paymentInfo?.provider || '-'}</div>
            <div>Transaction: {invoice.paymentInfo?.transactionId || '-'}</div>
            <div>Paid at: {formatDate(invoice.paymentInfo?.paidAt)}</div>
            <div>Status: {invoice.paymentInfo?.status || '-'}</div>
            <div>Payment option: {String(paymentBreakdown.paymentOptionSelected || 'full').replace(/_/g, ' ')}</div>
            <div>Partial percentage: {paymentBreakdown.partialPaymentPercentage ? `${paymentBreakdown.partialPaymentPercentage}%` : '-'}</div>
            <div>Paid now: {formatAmount(invoice.currency, paymentBreakdown.amountPaidNow ?? totals.amountPaid ?? 0)}</div>
            <div>Pending balance: {formatAmount(invoice.currency, paymentBreakdown.remainingAmount ?? totals.balanceDue ?? 0)}</div>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-500">Totals</p>
          <div className="mt-3 space-y-2 text-sm text-slate-700">
            <div className="flex items-center justify-between">
              <span>Base Price</span>
              <span>{formatAmount(invoice.currency, totals.basePrice ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Discount</span>
              <span>{formatAmount(invoice.currency, totals.discountAmount ?? totals.discount ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Taxable Subtotal</span>
              <span>{formatAmount(invoice.currency, totals.taxableSubtotal ?? totals.subtotal ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Mandatory fees</span>
              <span>{formatAmount(invoice.currency, totals.mandatoryFees ?? totals.fees ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>GST</span>
              <span>{formatAmount(invoice.currency, totals.gstAmount ?? totals.taxTotal ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Paid now</span>
              <span>{formatAmount(invoice.currency, totals.amountPaid ?? paymentBreakdown.amountPaidNow ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Pending balance</span>
              <span>{formatAmount(invoice.currency, totals.balanceDue ?? paymentBreakdown.remainingAmount ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between font-semibold text-slate-900">
              <span>Grand total</span>
              <span>{formatAmount(invoice.currency, totals.finalPayable ?? totalValue)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetail;
